<template>
  <CardContainer :title="cardTitle" :icon="icon" :is-small="true" :data-qa="dataQa?.title">
    <template v-if="hasVisibleViews" #status-chip>
      <div class="views-controls">
        <ViewsMenu
          @select="(id) => viewsStore.requestSelectView(id)"
          @create-new-view="viewsStore.createViewFromDefault()"
        />
        <SaveViewMenu @save-as-new="viewsStore.openSaveModal()" @save-edits="handleSaveEdits" />
      </div>
      <SaveViewModal
        :open="isSaveViewModalOpen"
        :server-duplicate-error="serverDuplicateError"
        @on-close="handleSaveModalClose"
        @save="handleSaveView"
        @clear-server-duplicate-error="viewsStore.clearServerDuplicateError()"
      />
      <CreateViewModal
        :open="isCreateViewModalOpen"
        :server-duplicate-error="serverDuplicateError"
        @on-close="handleSaveModalClose"
        @save="handleSaveView"
        @clear-server-duplicate-error="viewsStore.clearServerDuplicateError()"
      />
      <ManageViewsModal :open="manageModalOpen" />
    </template>

    <template #action-button>
      <div class="action-buttons">
        <ButtonItem
          v-if="!isProdEnv"
          :text="t('transactions.common.simulateAMoneyIn')"
          size="large"
          icon-position="left"
          left-icon="Input"
          theme="warning"
          :data-qa="dataQa?.simulateMoneyInBtn"
          @click="toggleSimulateMoneyInModal"
        />
        <ButtonItem
          v-if="hasHeader && checkPermission('sendPayment')"
          :text="t('transactions.list.sendPayementLabel')"
          icon-position="left"
          left-icon="SendReceive"
          size="large"
          :data-qa="dataQa?.sendPaymentToAnotherWalletBtn"
          @click="showSendPaymentModal"
        />
      </div>
    </template>

    <template #card-content>
      <div class="transactions-grid">
        <slot />
      </div>
    </template>
  </CardContainer>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ButtonItem, CardContainer } from '@lw-design-system/ui';
import ViewsMenu from '@/components/common/data-grid/views/ViewsMenu.vue';
import SaveViewMenu from '@/components/common/data-grid/views/SaveViewMenu.vue';
import SaveViewModal from '@/components/common/data-grid/views/SaveViewModal.vue';
import CreateViewModal from '@/components/common/data-grid/views/CreateViewModal.vue';
import ManageViewsModal from '@/components/common/data-grid/views/ManageViewsModal.vue';
import { useViewsStore } from '@/stores/views-store';
import { useApiErrorSnackbar } from '@/composables/common/useApiErrorSnackbar';
import type { SnackbarMessage } from '~/types';

const runtimeConfig = useConfig();
const { t } = useI18n();
const isProdEnv = runtimeConfig.value.public.lemonwayEnv.toLowerCase() === 'production';
const { showSendPaymentModal, toggleSimulateMoneyInModal } = useTransactions();
const viewsStore = useViewsStore();
const {
  loaded: viewsLoaded,
  views,
  saveModalOpen,
  pendingCreateConfig,
  serverDuplicateError,
  manageModalOpen
} = storeToRefs(viewsStore);

const isSaveViewModalOpen = computed(() => saveModalOpen.value && !pendingCreateConfig.value);
const isCreateViewModalOpen = computed(
  () => saveModalOpen.value && Boolean(pendingCreateConfig.value)
);
const { add } = useSnackbar();
const { notifyApiError } = useApiErrorSnackbar();

const handleSaveModalClose = () => {
  viewsStore.closeSaveModal();
  viewsStore.clearServerDuplicateError();
};

const handleSaveView = async (name: string) => {
  try {
    await viewsStore.requestSaveView(name);
    add({
      type: 'success',
      message: t('dataGrid.views.saveSuccess', { name })
    } as SnackbarMessage);
  } catch (error: unknown) {
    notifyApiError(error, { message: t('dataGrid.views.saveError') });
  }
};

const handleSaveEdits = async () => {
  const viewName = viewsStore.activeView?.name ?? '';
  try {
    await viewsStore.saveEdits();
    add({
      type: 'success',
      message: t('dataGrid.views.updateSuccess', { name: viewName })
    } as SnackbarMessage);
  } catch (error: unknown) {
    notifyApiError(error, { message: t('dataGrid.views.updateError') });
  }
};

const props = defineProps({
  cardTitle: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    required: true
  },
  hasHeader: {
    type: Boolean,
    default: true
  },
  hasViews: {
    type: Boolean,
    default: false
  },
  dataQa: {
    type: Object,
    default: undefined
  }
});

const hasVisibleViews = computed(
  () => props.hasViews && viewsLoaded.value && views.value.length > 0
);
</script>

<style scoped>
.transactions-grid {
  padding: 1.5rem;
}

.content-action-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.views-controls {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

:deep(.title-chip-container) {
  gap: 1.5rem;
}

.title {
  flex-shrink: 0;
}
</style>
